import Personnummer from "personnummer"
import { useCallback, useEffect, useState } from "react"
import { useFormContext } from "react-hook-form"
import { fetchPersonDetails } from "@/lib/api/checkoutForm"
import getInputValidationClasses from "@/lib/helpers/getInputValidationClasses"
import { input } from "@/components/common/form/input.module.css"
import Label from "@/components/common/form/label"
import { Spinner } from "@/components/common/loading/spinner"
import {
  ErrorMessage,
  HTMLError
} from "@/components/checkout-form/errorMessage"
import { useUserDetails, useActiveStep } from "@/components/checkout-form/store"
import classNames from "classnames"

function PinInputField({ orderSessionId, tooltip }) {
  const { userDetails, setUserDetails, clearPersonalDetails } = useUserDetails()
  const [isFetching, setIsFetching] = useState(false)
  const [hasFetched, setHasFetched] = useState(false)
  const [newFetch, setNewFetch] = useState(false)
  const [sparFailed, setSparFailed] = useState(false)
  const { checkMinor, checkYouth, minorText, notYouthText } = useActiveStep()
  const [htmlError, setHtmlError] = useState(null)

  const {
    watch,
    register,
    setError,
    clearErrors,
    setEditingUserDetails,
    control,
    setIsStepValidOverride
  } = useFormContext()

  const pin = watch("pin")
  const formValues = watch()

  useEffect(() => {
    clearErrors("pin")
    setHtmlError(null)

    if (!Personnummer.valid(pin)) {
      if (pin.length === 12) {
        setError("pin", {
          type: "custom",
          message: "Ange ett giltigt personnummer"
        })
      }

      resetUserDetails()
      return
    }

    const instance = Personnummer.parse(pin)
    const pinInLongFormat = instance.format(true)
    const age = instance.getAge()

    if (userDetails.pinInLongFormat === pinInLongFormat) {
      return
    }

    setUserDetails({ pinInLongFormat, age })

    if (checkMinor && age < 18) {
      setHtmlError(minorText)
      return
    }

    if (checkYouth && age > 26) {
      setHtmlError(notYouthText)
      return
    }

    handleFetchPersonDetails(pinInLongFormat)
  }, [pin]) // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (sparFailed) {
      const firstName = formValues?.firstName
      const lastName = formValues?.lastName
      const address = formValues?.address
      const zip = formValues?.zip
      const city = formValues?.city
      const fullUserDetails = firstName && lastName && address && zip && city
      setIsStepValidOverride(!!fullUserDetails)
      return
    }

    const { firstName, lastName, address, zip, city } = userDetails || {}
    const fullUserDetails = firstName && lastName && address && zip && city

    if (fullUserDetails) {
      setIsStepValidOverride(true)
      return
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userDetails, sparFailed, formValues])

  const handleFetchPersonDetails = useCallback(
    async (thePin) => {
      if (isFetching) {
        return
      }

      setIsFetching(true)
      clearErrors("pin")

      const personDetails = await fetchPersonDetails(+thePin, orderSessionId)
      if (!personDetails?.error) {
        setIsStepValidOverride(true)
        setUserDetails(personDetails)
        setEditingUserDetails(false)
        setSparFailed(false)
      } else {
        setEditingUserDetails(true)
        setSparFailed(true)
      }
      setNewFetch(true)
      setIsFetching(false)
      setHasFetched(true)
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [isFetching, setUserDetails, orderSessionId]
  )

  const validateSpar = () => {
    if (!hasFetched) {
      return true
    }

    if (sparFailed) {
      return true
    }

    const { firstName, lastName, address, zip, city } = userDetails || {}
    const fullUserDetails = firstName && lastName && address && zip && city

    return !!fullUserDetails
  }

  const resetUserDetails = () => {
    setHasFetched(false)
    setSparFailed(false)
    clearPersonalDetails()
    setEditingUserDetails(false)
    setHtmlError(null)
  }

  return (
    <div className="px-3 mb-3 w-full lg:mb-6">
      <Label
        name="pin"
        tooltip={tooltip}
        description="12 siffror - ÅÅÅÅMMDDXXXX">
        Personnummer
      </Label>
      <div className="relative">
        <input
          type="text"
          inputMode="numeric"
          {...register("pin", {
            required: true,
            validate: {
              valid: (value) =>
                Personnummer.valid(value) || "Ange ett giltigt personnummer",
              spar: () => (sparFailed ? true : validateSpar())
            }
          })}
          id="pin"
          autoComplete="on"
          placeholder="Fyll i ditt personnummer"
          onKeyDown={() => {
            if (newFetch) {
              setNewFetch(false)
            }
          }}
          className={classNames([
            input,
            getInputValidationClasses("pin", control)
          ])}
        />
        {isFetching && (
          <Spinner
            size="small"
            className="absolute top-[0.74em] right-3"
            sizeClasses="w-6 h-6 border-3"
          />
        )}
      </div>
      <ErrorMessage name="pin" />
      <HTMLError message={htmlError} />
    </div>
  )
}

export default PinInputField
